import { useAuthRestApi } from '../common';
import {
    IOrganization,
    IOrganizationInvitation,
    IOrganizationMember,
    IOrganizationsApi,
    IOrganizationUpdate
} from '@vivli/features/organizations/infrastructure/interface';
import { Observable } from 'rxjs';
import { IOrganizationChannel } from '@vivli/features/chat/infrastructure/interface';
import { IOrganizationRoles, IVivliRoleDef } from '@vivli/shared/infrastructure/interface';

export const useOrganizationsApi = (): IOrganizationsApi => {
    const { handleGet, handlePost, handlePut, handleDelete } = useAuthRestApi();
    const getOrganization = (organizationId: string): Observable<IOrganization> => {
        return handleGet<IOrganization>(`/orgs/${organizationId}`);
    };

    const getOrganizations = (): Observable<IOrganization[]> => {
        return handleGet<IOrganization[]>('/orgs');
    };

    const getOrganizationMembers = (organizationId: string, includeDisabled?: boolean): Observable<IOrganizationMember[]> => {
        return handleGet<IOrganizationMember[]>(`/orgs/${organizationId}/members${includeDisabled ? '/disabled' : ''}`);
    };

    const getIrpApproverOrganizations = (): Observable<IOrganization[]> => {
        return handleGet<IOrganization[]>('/orgs/irpApprovers');
    };

    const getAnnotatorOrganizations = (): Observable<IOrganization[]> => {
        return handleGet<IOrganization[]>('/orgs/annotators');
    };

    const getQaReviewerOrganizations = (): Observable<IOrganization[]> => {
        return handleGet<IOrganization[]>('/orgs/qaReviewers');
    };

    const addOrganization = (organization: IOrganization, isAmr: boolean): Observable<IOrganizationUpdate> => {
        return handlePost<IOrganizationUpdate>(`/orgs/${isAmr}`, organization);
    };

    const addForSharedDomainOrganization = (organization: IOrganization, isAmr: boolean): Observable<IOrganizationUpdate> => {
        return handlePost<IOrganizationUpdate>(`/orgs/${isAmr}/ForSharedDomain`, organization);
    };

    const updateOrganization = (organization: IOrganization, isAmr: boolean): Observable<IOrganizationUpdate> => {
        return handlePut<IOrganizationUpdate>(`/orgs/${isAmr}`, organization);
    };

    const updateForSharedDomainOrganization = (organization: IOrganization, isAmr: boolean): Observable<IOrganizationUpdate> => {
        return handlePut<IOrganizationUpdate>(`/orgs/${isAmr}/ForSharedDomain`, organization);
    };

    const inviteOrganizationMember = (
        organizationId: string,
        invite: IOrganizationInvitation,
        isAmr: boolean
    ): Observable<IOrganizationInvitation> => {
        return handlePost<IOrganizationInvitation>(`/orgs/${organizationId}/invitations/${isAmr}`, invite);
    };

    const setOrganizationMemberRights = (
        organizationId: string,
        memberUserId: string,
        memberEmail: string,
        memberRights: IOrganizationRoles
    ): Observable<IOrganizationMember> => {
        return handlePost<IOrganizationMember>(
            `/orgs/${organizationId}/members/${memberUserId}?email=${encodeURIComponent(memberEmail)}`,
            memberRights
        );
    };

    const removeOrganizationMember = (organizationId: string, memberUserId: string): Observable<void> => {
        return handleDelete(`/orgs/${organizationId}/members/${memberUserId}`);
    };

    const getDraftBulkUploadId = (organizationId: string, isAmr: boolean): Observable<string> => {
        return handlePost<string>(`/orgs/${organizationId}/uploadPackage/${isAmr}`, organizationId);
    };

    const getOrganizationChannels = (dataRequestId: string): Observable<IOrganizationChannel[]> => {
        return handleGet<IOrganizationChannel[]>(`/dataRequests/${dataRequestId}/myorgchannels`);
    };

    const joinMember = (): Observable<string[]> => {
        return handleGet<string[]>('/orgs/joinMember');
    };

    const updateOrganizationMembers = (members: IOrganizationMember[], organizationId: string): Observable<IOrganizationMember[]> => {
        return handlePut<IOrganizationMember[]>(`/orgs/${organizationId}/members/update`, members);
    };

    const getRoles = (): Observable<[IVivliRoleDef]> => {
        return handleGet<[IVivliRoleDef]>('/VivliConfig/roles');
    };

    return {
        getOrganization,
        getOrganizations,
        getOrganizationMembers,
        getIrpApproverOrganizations,
        getAnnotatorOrganizations,
        getQaReviewerOrganizations,
        addOrganization,
        addForSharedDomainOrganization,
        updateOrganization,
        updateForSharedDomainOrganization,
        inviteOrganizationMember,
        setOrganizationMemberRights,
        removeOrganizationMember,
        getDraftBulkUploadId,
        getOrganizationChannels,
        joinMember,
        updateOrganizationMembers,
        getRoles
    };
};
